import React from 'react';

const W = 800, H = 160, PAD = { top: 16, right: 16, bottom: 24, left: 56 };

function fmt(v) {
    if (Math.abs(v) >= 1000) return (v / 1000).toFixed(1) + 'k';
    return v.toFixed(0);
}

function fmtDate(ts) {
    const d = new Date(ts);
    return `${d.getDate()}/${d.getMonth() + 1}`;
}

function EquityCurve({ data, onPointClick }) {
    if (!data || data.length < 2) {
        return (
            <div className='card card-body border-0 shadow mb-4 text-center text-muted py-5'>
                Nenhum dado de performance ainda — as ordens executadas aparecerão aqui.
            </div>
        );
    }

    const values = data.map(d => d.value);
    const minV = Math.min(...values, 0);
    const maxV = Math.max(...values, 0);
    const range = maxV - minV || 1;

    const innerW = W - PAD.left - PAD.right;
    const innerH = H - PAD.top - PAD.bottom;

    function xOf(i) { return PAD.left + (i / (data.length - 1)) * innerW; }
    function yOf(v) { return PAD.top + innerH - ((v - minV) / range) * innerH; }

    const points = data.map((d, i) => `${xOf(i)},${yOf(d.value)}`).join(' ');
    const zeroY = yOf(0);
    const profitable = data[data.length - 1].value >= 0;
    const color = profitable ? '#1aae6f' : '#f5365c';

    const areaPoints = `${xOf(0)},${zeroY} ${points} ${xOf(data.length - 1)},${zeroY}`;

    // Label ticks: ~5 evenly spaced dates
    const tickIdxs = [0, Math.floor(data.length * 0.25), Math.floor(data.length * 0.5), Math.floor(data.length * 0.75), data.length - 1];
    const yTicks = [minV, (minV + maxV) / 2, maxV].map(v => Math.round(v * 100) / 100);

    return (
        <div className='card card-body border-0 shadow mb-4'>
            <div className='d-flex justify-content-between align-items-center mb-2'>
                <h6 className='mb-0'>Equity Curve</h6>
                <span className={`badge py-1 ${profitable ? 'bg-success' : 'bg-danger'}`}>
                    {profitable ? '+' : ''}{data[data.length - 1].value.toFixed(2)} USDT
                </span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} width='100%' style={{ overflow: 'visible' }}>
                {/* Zero baseline */}
                <line x1={PAD.left} y1={zeroY} x2={W - PAD.right} y2={zeroY}
                    stroke='#adb5bd' strokeWidth='1' strokeDasharray='4 3' />

                {/* Area fill */}
                <polygon points={areaPoints} fill={color} fillOpacity='0.12' />

                {/* Line */}
                <polyline points={points} fill='none' stroke={color} strokeWidth='2' strokeLinejoin='round' />

                {/* Clickable points — perdas em vermelho destacado, ganhos cinza discreto.
                    Clica → onPointClick(orderId) abre post-mortem. */}
                {onPointClick && data.map((d, i) => {
                    if (!d.orderId) return null;
                    const isLoss = d.pnlPct != null && d.pnlPct < 0;
                    return (
                        <circle
                            key={d.orderId}
                            cx={xOf(i)}
                            cy={yOf(d.value)}
                            r={isLoss ? 4 : 3}
                            fill={isLoss ? '#f5365c' : '#adb5bd'}
                            fillOpacity={isLoss ? 0.9 : 0.4}
                            stroke='white'
                            strokeWidth='1'
                            style={{ cursor: 'pointer' }}
                            onClick={() => onPointClick(d.orderId)}>
                            <title>
                                {d.symbol} {d.side} · {new Date(d.time).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                                {d.pnlPct != null ? `\nP&L: ${d.pnlPct >= 0 ? '+' : ''}${d.pnlPct}%` : ''}
                                {'\n(clique pra investigar)'}
                            </title>
                        </circle>
                    );
                })}

                {/* Y axis labels */}
                {yTicks.map((v, i) => (
                    <text key={i} x={PAD.left - 4} y={yOf(v) + 4} textAnchor='end'
                        fontSize='10' fill='#6c757d'>{fmt(v)}</text>
                ))}

                {/* X axis labels */}
                {tickIdxs.filter(i => i < data.length).map(i => (
                    <text key={i} x={xOf(i)} y={H - 4} textAnchor='middle'
                        fontSize='10' fill='#6c757d'>{fmtDate(data[i].time)}</text>
                ))}
            </svg>
        </div>
    );
}

export default EquityCurve;
